import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Filter } from "./Filter";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Fetch, ILazyParams } from "../../utilities/fetch";
import { EmptyList, TFormState, TListState } from "../../shared/modal";
import useToggle from "../../hooks/useToggle";
import { Search } from "../../shared/Search";
import { EmptyUser, EmptyUserFilter, TUser, TUserFilter, TUserRecord, WorkTypeLabel } from "./modal";
import { Form } from "./Form";
import { useToast } from "../../context/toastProvider";

type TL = TListState<TUser>;
type TF = TFormState<TUserRecord>;

export const Table = () => {
    const [table, setTable] = useState(EmptyList as TL);
    const { showToast } = useToast();
    const [globalSearch, setGlobalSearch] = useState<string | null>(null);
    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });
    const [filters, setFilters] = useState<TUserFilter>(EmptyUserFilter);

    const [form, setForm] = useState({ open: false, data: EmptyUser } as TF);
    const hideForm = () => setForm({ open: false, data: EmptyUser });
    const [r, reload] = useToggle();

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTable(prev => ({ ...prev, loading: true }));
        const result = await Fetch<TL>("users",
            {
                query: new URLSearchParams({
                    lazyParams: JSON.stringify(lazyParams),
                    filters: JSON.stringify(filters),
                    ...(globalSearch ? { search: globalSearch } : {})
                }).toString()
            });
        if (result) {
            setTable({ data: result.data, count: result.count, loading: false });
        }
        setTable(prev => ({ ...prev, loading: false }));
    }

    useEffect(() => {
        loadTableData();
    }, [globalSearch, lazyParams, filters, r]);

    const openNew = () => {
        setForm({ open: true, data: EmptyUser });
    };

    const openEdit = (r: TUser) => {
        const data: TUserRecord = { ...r, roles: [], };
        setForm({ open: true, data: data });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    <Button label="New" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
                </div>
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Export" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} />
            </React.Fragment>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">Manage Users</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <Search setSearch={setGlobalSearch} />
            </span>
        </div>
    );

    const toggleStatus = async (userId: number) => {
        const res = await Fetch(`Users/toggle/status/${userId}`, { method: 'PUT', });
        if (res) {
            showToast({ severity: 'success', detail: "User Status Changed" });
            reload();
        }
    };
    const toggleWorkFrom = async (userId: number) => {
        const res = await Fetch(`Users/toggle/workFrom/${userId}`, { method: 'PUT', });
        if (res) {
            showToast({ severity: 'success', detail: "Work From Changed" });
            reload();
        }
    };

    const userStatus = (rowData: TUser) => {
        return (
            <span className="cursor-pointer text-cyan-700" onClick={() => { if (rowData.id) toggleStatus(rowData.id) }}>{rowData.status ? "Active" : "Inactive"}</span>
        );
    };
    const workFrom = (rowData: TUser) => {
        return (
            <span className="cursor-pointer text-cyan-700" onClick={() => { if (rowData.id) toggleWorkFrom(rowData.id) }}>{WorkTypeLabel[rowData.workFrom]}</span>
        );
    };

    const actionBodyTemplate = (rowData: TUser) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil text-primary" className="p-button-rounded p-button-success p-button-text mr-1" onClick={() => openEdit(rowData)} />
            </div>
        );
    };

    return (
        <>
            <Accordion>
                <AccordionTab header="Filter">
                    <Filter filters={filters} setFilters={(filters: TUserFilter) => setFilters(filters)} loading={table.loading} />
                </AccordionTab>
            </Accordion>
            <Toolbar className="mb-1 py-0 mt-1" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt} size="small" exportFilename={format(new Date(), 'dd-MM-yyyy hh_mm_ss a').toUpperCase()}
                value={table.data} lazy dataKey="id" showGridlines stripedRows responsiveLayout="scroll"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={table.count} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                emptyMessage="No Users found."
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={table.loading}
            >
                <Column field="name" header="Name" sortable />
                <Column field="email" header="EmailID" sortable />
                <Column field="phoneNo" header="Phone NO" sortable />
                <Column field="workFrom" header="Work From" sortable body={r => workFrom(r)} />
                <Column field="status" header="Status" sortable body={r => userStatus(r)} />
                <Column body={actionBodyTemplate} />
            </DataTable>
            <Form open={form.open} data={form.data} hideDialog={hideForm} reload={reload} />
        </>
    );
}
